import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { LuChevronDown, LuUsers, LuSearch } from 'react-icons/lu';
import { useFilters } from '../context/FilterContext';
import employeeService from '../services/employeeService';
import useEscapeKey from '../hooks/useEscapeKey';
import './WorkContextBar.css';

const destinations = [
  ['/timesheet', 'Timesheet'], ['/timeline', 'Timeline'], ['/screenshots', 'Screenshots'],
  ['/timelapse-videos', 'Timelapses'], ['/apps-history', 'App history'],
];

function ScopeBar() {
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const { teamFilterId } = useFilters();
  const member = params.get('member') || params.get('user') || '';
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(false); setMembers([]);
    employeeService.getAllEmployees(teamFilterId ? { team: teamFilterId } : {}).then((data) => {
      if (!cancelled) setMembers(Array.isArray(data) ? data : data?.results || []);
    }).catch(() => { if (!cancelled) setError(true); }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [teamFilterId, attempt]);

  const selected = members.find((item) => String(item.id) === member || item.full_name?.toLowerCase() === member.toLowerCase());

  const select = (value) => {
    const next = new URLSearchParams(params);
    next.delete('user'); next.delete('member');
    if (value) next.set('member', value);
    setParams(next, { replace: true });
    setIsOpen(false);
    setSearchQuery('');
  };

  useEscapeKey(() => setIsOpen(false), isOpen);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleOutsideClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isOpen]);

  const defaultLabel = location.pathname.startsWith('/apps-history') ? 'Choose an employee' : 'All employees';
  const selectedName = selected ? (selected.full_name || selected.name || `Employee ${selected.id}`) : null;
  const displayLabel = loading ? 'Loading…' : selectedName || (member ? 'Unavailable' : defaultLabel);

  const filteredMembers = members.filter((item) => {
    if (!searchQuery.trim()) return true;
    return (item.full_name || item.name || `Employee ${item.id}`).toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Only shared scope travels. Page-specific search, pagination and view modes stay local.
  const shared = new URLSearchParams();
  ['period', 'from', 'to', 'team'].forEach((key) => { if (params.has(key)) shared.set(key, params.get(key)); });
  if (member) shared.set('member', selected ? String(selected.id) : member);

  return (
    <section className="work-context-bar" aria-label="Connected work views">
      <div className="work-context-person">
        <label htmlFor="work-context-member">Employee</label>

        {/* Hidden native select preserves label association for e2e tests */}
        <select
          id="work-context-member"
          className="work-context-hidden-select"
          value={selected ? String(selected.id) : member}
          disabled={loading || error}
          onChange={(event) => select(event.target.value)}
          tabIndex={-1}
          aria-hidden="true"
        >
          <option value="">{loading ? 'Loading…' : defaultLabel}</option>
          {member && !selected && <option value={member}>Unavailable</option>}
          {members.map((item) => (
            <option key={item.id} value={String(item.id)}>
              {item.full_name || item.name || `Employee ${item.id}`}
            </option>
          ))}
        </select>

        {/* Custom dropdown — same pattern as the Team filter in the header */}
        <div className="dropdown-container wcb-employee-filter" ref={dropdownRef}>
          <button
            type="button"
            className="header-btn"
            onClick={() => !loading && !error && setIsOpen((prev) => !prev)}
            disabled={loading || error}
            aria-haspopup="true"
            aria-expanded={isOpen}
            aria-label={`Employee: ${displayLabel}`}
            title={displayLabel}
          >
            <span className="team-icon"><LuUsers size={16} /></span>
            <span className="wcb-employee-label">{displayLabel}</span>
            <span className="dropdown-icon"><LuChevronDown size={14} /></span>
          </button>

          {isOpen && (
            <div className="team-dropdown wcb-employee-dropdown" role="group" aria-label="Select employee">
              {/* Search — shown when there are enough members to scroll */}
              {members.length > 5 && (
                <div className="wcb-employee-search">
                  <LuSearch size={13} className="wcb-search-icon" />
                  <input
                    type="text"
                    className="wcb-search-input"
                    placeholder="Search…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                  />
                </div>
              )}

              {/* "All employees" option */}
              <button
                type="button"
                aria-pressed={!member}
                className={`team-option ${!member ? 'selected' : ''}`}
                onClick={() => select('')}
              >
                {defaultLabel}
              </button>

              {/* Unavailable placeholder when member param has no match */}
              {member && !selected && (
                <button
                  type="button"
                  aria-pressed={true}
                  className="team-option selected"
                  onClick={() => select(member)}
                >
                  Selected employee unavailable
                </button>
              )}

              {/* Employee list */}
              {loading && (
                <div className="team-dropdown-state" role="status">Loading employees…</div>
              )}
              {!loading && error && (
                <div className="team-dropdown-state team-dropdown-state--error" role="alert">
                  <span>Failed to load employees.</span>
                  <button type="button" onClick={() => setAttempt((v) => v + 1)}>Retry</button>
                </div>
              )}
              {!loading && !error && filteredMembers.map((item) => {
                const isSelected = String(item.id) === (selected ? String(selected.id) : member);
                const name = item.full_name || item.name || `Employee ${item.id}`;
                return (
                  <button
                    key={item.id}
                    type="button"
                    aria-pressed={isSelected}
                    className={`team-option ${isSelected ? 'selected' : ''}`}
                    onClick={() => select(String(item.id))}
                  >
                    {name}
                  </button>
                );
              })}
              {!loading && !error && filteredMembers.length === 0 && (
                <div className="team-dropdown-state">No employees found.</div>
              )}
            </div>
          )}
        </div>
      </div>

      <nav aria-label="Related work views">
        {destinations.map(([path, label]) => (
          <Link
            key={path}
            to={`${path}${shared.size ? `?${shared}` : ''}`}
            aria-current={location.pathname.startsWith(path) ? 'page' : undefined}
          >
            {label}
          </Link>
        ))}
      </nav>
      <span className="work-context-hint">Employee, team, and dates travel with you</span>
    </section>
  );
}

export default function WorkContextBar() {
  const { pathname } = useLocation();
  return destinations.some(([path]) => pathname === path || pathname.startsWith(`${path}/`)) ? <ScopeBar /> : null;
}
